import React from "react";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { cn } from "../../utils/cn";

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface PageHeaderProps {
  title: string;
  description?: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  showBack?: boolean;
  onBack?: () => void;
  backTo?: string;
  actions?: React.ReactNode;
  className?: string;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  breadcrumbs,
  showBack,
  onBack,
  backTo,
  actions,
  className = "",
}) => {
  const navigate = useNavigate();
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "mobile";

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className={cn("mb-5", className)}>
      {/* 1. Breadcrumbs: horizontally scrollable without breaking on small screens */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="flex items-center gap-1.5 sm:gap-2 mb-1.5 overflow-x-auto no-scrollbar py-0 leading-none">
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={crumb.label}>
              {i > 0 && (
                <ChevronRight className="w-3 h-3 text-text-tertiary flex-shrink-0" />
              )}
              {i === breadcrumbs.length - 1 ? (
                <span className="text-[12px] font-bold text-primary-600 truncate max-w-[160px] sm:max-w-[240px] leading-tight">
                  {crumb.label}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => crumb.path && navigate(crumb.path)}
                  className="text-[12px] font-semibold text-text-tertiary hover:text-primary-600 transition-colors truncate max-w-[160px] sm:max-w-[240px] leading-tight cursor-pointer border-none bg-transparent p-0"
                >
                  {crumb.label}
                </button>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      {/* 2. Header Row: Vertical stack on mobile -> Horizontal split on desktop */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        {/* Left: Title & Subtitle */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {showBack && (
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center justify-center w-8 h-8 rounded-[9px] text-slate-500 hover:bg-slate-100 hover:text-primary-700 transition-all duration-200 flex-shrink-0 cursor-pointer border-none bg-transparent"
              aria-label="Go back"
            >
              <ArrowLeft className="w-[18px] h-[18px]" />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="text-[20px] sm:text-[24px] lg:text-[26px] font-extrabold text-text-primary tracking-tight leading-tight">
              {title}
            </h1>
            {description && (
              <p className="text-[12px] sm:text-[13px] text-text-secondary mt-0.5 leading-normal">
                {description}
              </p>
            )}
          </div>
        </div>

        {/* Right: Actions Toolbar (Auto-aligns right on desktop, compact wrap on mobile) */}
        {actions && (
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-start sm:justify-end flex-none">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
